import { MAPS, TUNING, WORLD_VERSION } from '@cloudpearl/config';
import type { MapId } from '@cloudpearl/config';
import { Sim } from './sim';
import type { SimInput } from './sim';
import { seedFromCode } from './seed';

/**
 * Input recording. One byte per tick: bit 7 = hold, bits 0–6 = steer + steerSteps.
 * Stored run-length encoded with LEB128 run lengths, then base64url. A two-minute run is
 * typically a few KB, small enough for localStorage ghosts or server-side score verification.
 */
export class InputRecorder {
  private bytes = new Uint8Array(4096);
  private n = 0;

  get length(): number {
    return this.n;
  }

  clear(): void {
    this.n = 0;
  }

  push(input: SimInput): void {
    if (this.n >= this.bytes.length) {
      const b = new Uint8Array(this.bytes.length * 2);
      b.set(this.bytes);
      this.bytes = b;
    }
    this.bytes[this.n++] = packInput(input);
  }

  encode(): string {
    return encodeInputs(this.bytes.subarray(0, this.n));
  }
}

export function packInput(input: SimInput): number {
  const steer = Math.max(-TUNING.steerSteps, Math.min(TUNING.steerSteps, Math.round(input.steer))) + TUNING.steerSteps;
  return ((input.hold ? 1 : 0) << 7) | steer;
}

export function unpackInput(b: number, out: SimInput): SimInput {
  out.hold = (b & 0x80) !== 0;
  out.steer = (b & 0x7f) - TUNING.steerSteps;
  return out;
}

export function encodeInputs(ticks: Uint8Array): string {
  const out: number[] = [];
  let i = 0;
  while (i < ticks.length) {
    const v = ticks[i]!;
    let run = 1;
    while (i + run < ticks.length && ticks[i + run] === v) run++;
    out.push(v);
    let r = run;
    while (r >= 0x80) {
      out.push((r & 0x7f) | 0x80);
      r >>>= 7;
    }
    out.push(r);
    i += run;
  }
  return toBase64Url(Uint8Array.from(out));
}

export function decodeInputs(data: string): Uint8Array {
  const bytes = fromBase64Url(data);
  const ticks: number[] = [];
  let i = 0;
  while (i < bytes.length) {
    const v = bytes[i++]!;
    let run = 0;
    let shift = 0;
    for (;;) {
      const b = bytes[i++]!;
      run |= (b & 0x7f) << shift;
      if ((b & 0x80) === 0) break;
      shift += 7;
    }
    for (let k = 0; k < run; k++) ticks.push(v);
  }
  return Uint8Array.from(ticks);
}

export interface ReplayResult {
  distance: number;
  ticks: number;
  alive: boolean;
}

/** Re-simulate a run from its inputs. Used to verify a claimed score or drive a ghost. */
export function verifyReplay(mapId: MapId, seedCode: string, data: string): ReplayResult {
  const sim = new Sim(seedFromCode(seedCode, WORLD_VERSION), MAPS[mapId]);
  sim.start();
  const ticks = decodeInputs(data);
  const input: SimInput = { hold: false, steer: 0 };
  for (let i = 0; i < ticks.length && sim.alive; i++) sim.step(unpackInput(ticks[i]!, input));
  return { distance: sim.distance, ticks: sim.tick, alive: sim.alive };
}

const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

function toBase64Url(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const a = bytes[i]!;
    const b = bytes[i + 1] ?? 0;
    const c = bytes[i + 2] ?? 0;
    const n = (a << 16) | (b << 8) | c;
    s += B64[(n >> 18) & 63]! + B64[(n >> 12) & 63]!;
    if (i + 1 < bytes.length) s += B64[(n >> 6) & 63]!;
    if (i + 2 < bytes.length) s += B64[n & 63]!;
  }
  return s;
}

function fromBase64Url(s: string): Uint8Array {
  const out: number[] = [];
  let buf = 0;
  let bits = 0;
  for (let i = 0; i < s.length; i++) {
    const v = B64.indexOf(s[i]!);
    if (v < 0) continue;
    buf = (buf << 6) | v;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      out.push((buf >> bits) & 0xff);
    }
  }
  return Uint8Array.from(out);
}
